// Time & Action Plan export (PDF A4 landscape or Excel): the columns that can be exported and what each cell says.
// The same wording is used by the PDF (made in the browser) and the Excel file (made by the server), from the rows
// /api/org/critical returns. Only the lines shown on screen are exported (the screen sends their ids, in its order).

export type CpxTone = "ok" | "bad" | "amber" | "late" | "grey" | "stp" | "na";
/** One cell: the words (Excel and PDF), and for the stages a tone, the round and the date for the PDF's icon. */
export interface CpxCell { text: string; tone?: CpxTone; round?: number; date?: string; /** "due" before the date. */ due?: boolean; num?: number }
export interface CpxColumn { key: string; label: string; standard: boolean; stage?: boolean; num?: boolean; date?: boolean }
export interface CpxNames { sample?: string; fit?: string; pps?: string; seal?: string; shipment?: string }
/** Which columns the person may see (the `columns` of /api/org/critical) and whether cost / amount are shown. */
export interface CpxAccess { columns: Record<string, boolean>; amounts: boolean; fabricTest: boolean; names?: CpxNames }

/** Every exportable column, in the order they appear in the file. `standard` = ticked by default. */
export function exportColumns(x: CpxAccess): CpxColumn[] {
  const c = x.columns || {};
  const n = x.names || {};
  const list: Array<CpxColumn | false> = [
    { key: "po", label: "PO#", standard: true },
    { key: "product", label: "Product", standard: true },
    { key: "desc", label: "Description", standard: false },
    !!c.supplier && { key: "supplier", label: "Supplier", standard: false },
    !!c.agent && { key: "agent", label: "Agent", standard: false },
    !!c.orderDate && { key: "orderDate", label: "Order date", standard: false, date: true },
    { key: "qty", label: "Total qty", standard: true, num: true },
    x.amounts && !!c.costUsd && { key: "cost", label: "Cost", standard: false, num: true },
    x.amounts && !!c.amountUsd && { key: "amount", label: "Amount", standard: false, num: true },
    !!c.sample && { key: "labdip", label: "Lab dip", standard: true, stage: true },
    !!c.sample && { key: "sample", label: n.sample || "Sample", standard: true, stage: true },
    !!c.fitSample && { key: "fit", label: n.fit || "Fit sample", standard: true, stage: true },
    !!c.pps && { key: "pps", label: n.pps || "PPS", standard: true, stage: true },
    !!c.goldSeal && { key: "seal", label: n.seal || "Gold seal", standard: true, stage: true },
    !!c.bulkProduction && x.fabricTest && { key: "fabric", label: "Fabric test", standard: true, stage: true },
    !!c.bulkProduction && { key: "bulk", label: "Bulk", standard: true, stage: true },
    !!c.qc && { key: "qc", label: "QC", standard: true, stage: true },
    !!c.readyToShip && { key: "ready", label: "Ready", standard: true, stage: true },
    !!c.etc && { key: "etc", label: "ETC", standard: true, date: true },
    !!c.etd && { key: "etd", label: "ETD", standard: true, date: true },
    !!c.etd && { key: "originalEtd", label: "Original ETD", standard: false, date: true },
    !!c.eta && { key: "eta", label: "ETA", standard: true, date: true },
    !!c.received && { key: "ship", label: "Shipment status", standard: true },
    !!c.received && { key: "received", label: "Received date", standard: false, date: true },
    !!c.status && { key: "status", label: "Status", standard: true },
  ];
  return list.filter(Boolean) as CpxColumn[];
}

/** dd/mm/yy, as on screen. */
export const cpxDate = (v?: string | null): string => {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1].slice(-2)}` : "";
};
const NA: CpxCell = { text: "—", tone: "na" };
const OK_WORD: Record<string, string> = { qc: "Passed", fabric: "Passed" };
const BAD_WORD: Record<string, string> = { qc: "Failed", fabric: "Failed" };
const roundTxt = (n?: number) => (n && n > 1 ? ` (R${n})` : "");

/** Lab dip, Sample, Fit, PPS, Fabric test and QC: the current round (the badge the screen shows as an icon). */
function badgeCell(r: any, stage: string, today: string): CpxCell {
  if (["sample", "fit", "pps"].includes(stage) && !r.sampling) return NA;
  const b = r.badges?.[stage];
  if (!b) return NA;
  // A failed fabric lot's badge carries the next round; the cross shows the round that failed.
  const round = b.tone === "bad" && b.status === "due" ? b.round - 1 : b.round;
  if (b.tone === "ok") return { text: `${OK_WORD[stage] || "Approved"}${roundTxt(round)}${b.date ? ` ${cpxDate(b.date)}` : ""}`, tone: "ok", round, date: b.date };
  if (b.tone === "bad") return { text: `${BAD_WORD[stage] || "Rejected"}${roundTxt(round)}`, tone: "bad", round };
  if (b.tone === "amber") {
    const late = !!b.due && b.due < today;
    if (b.due) return { text: `${late ? "Overdue" : "Due"}${roundTxt(round)} ${cpxDate(b.due)}`, tone: late ? "late" : "amber", round, date: b.due, due: true };
    return { text: `In progress${roundTxt(round)}`, tone: "amber", round };
  }
  if (b.status === "waived") return { text: stage === "fabric" ? "Waived" : "STP", tone: "stp" };
  if (b.status === "skipped") return { text: "Skipped", tone: "stp" };   // QC skipped by an Admin (never a pass)
  if (b.due && b.due < today) return { text: `Overdue ${cpxDate(b.due)}`, tone: "late", date: b.due, due: true };
  return { text: "Not started", tone: "grey" };
}
function sealCell(r: any, names: CpxNames): CpxCell {
  if (!r.sampling) return NA;
  if (r.goldSeal) return { text: `Approved ${cpxDate(r.goldSeal.date)}`.trim(), tone: "ok", date: r.goldSeal.date };
  if (r.stp) {
    const extra = (["shade", "shipment"] as const).find((k) => r.stp[k] && r.stp[k].status !== "Approved");
    if (extra) return { text: `STP · ${extra === "shade" ? "Shade" : names.shipment || "Shipment sample"}: ${r.stp[extra].status}`, tone: r.stp[extra].status === "Rejected" ? "bad" : "amber" };
    return { text: "STP", tone: "stp" };
  }
  if (r.sealMissing) return { text: "Missing", tone: "bad" };
  return { text: "Not started", tone: "grey" };
}
function shipStatus(r: any): string {
  if (!r.workflow?.readyToShip?.value) return "—";
  const legacy = !r.shipment?.shipments?.length && r.workflow.received?.value;
  // Shipped over the PO (01/11/2026) is shown with the status, e.g. "Loading · +76 over PO".
  const over = Number(r.shipment?.overQty) > 0 ? ` · +${Number(r.shipment.overQty)} over PO` : "";
  return (legacy ? "Received" : r.shipment?.status || "Loading") + over;
}

/** The value of one column for one line. `today` is the organisation's day (yyyy-mm-dd), for "Overdue". */
export function exportCell(r: any, key: string, today: string, names: CpxNames = {}): CpxCell {
  const w = r.workflow || {};
  switch (key) {
    case "po": return { text: String(r.poNumber || "") };
    case "product": return { text: [r.code, r.colour].filter(Boolean).join(" · ") };
    case "desc": return { text: String(r.description || "") };
    case "supplier": return { text: String(r.supplier || "") };
    case "agent": return { text: String(r.agent || "") };
    case "orderDate": return { text: cpxDate(r.orderDate), date: r.orderDate || undefined };
    case "qty": return { text: `${Number(r.quantity || 0).toLocaleString("en-GB")}${r.unit && r.unit !== "pcs" ? ` ${r.unit}` : ""}`, num: Number(r.quantity || 0) };
    case "cost": return r.unitPrice === undefined || r.unitPrice === null ? { text: "" } : { text: `${r.currency || ""} ${Number(r.unitPrice).toFixed(2)}`.trim(), num: Number(r.unitPrice) };
    case "amount": return r.amount === undefined || r.amount === null ? { text: "" } : { text: `${r.currency || ""} ${Number(r.amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim(), num: Number(r.amount) };
    case "labdip": case "sample": case "fit": case "pps": case "fabric": case "qc": return badgeCell(r, key, today);
    case "seal": return sealCell(r, names);
    case "bulk": {
      const v = w.bulk?.status || "Waiting";
      if (v === "Complete") return { text: `Complete ${cpxDate(w.bulk?.date)}`.trim(), tone: "ok", date: w.bulk?.date };
      if (v === "In Progress") return { text: "In progress", tone: "amber" };
      return { text: "Not started", tone: "grey" };
    }
    case "ready": return w.readyToShip?.value ? { text: `Ready ${cpxDate(w.readyToShip?.date)}`.trim(), tone: "ok", date: w.readyToShip?.date } : { text: "Not yet", tone: "grey" };
    case "etc": return { text: cpxDate(r.etc), date: r.etc || undefined };
    case "etd": { const v = r.workingEtd || r.etd; return { text: cpxDate(v), date: v || undefined }; }
    case "originalEtd": return { text: cpxDate(r.etd), date: r.etd || undefined };
    case "eta": return { text: cpxDate(r.eta), date: r.eta || undefined };
    case "ship": return { text: shipStatus(r) };
    case "received": { const v = r.shipment?.receivedDate || (w.received?.value ? w.received?.date : ""); return { text: cpxDate(v), date: v || undefined }; }
    case "status": return { text: String(r.status || "") };
    default: return { text: "" };
  }
}

